import { useEffect, useMemo, useRef } from 'react';
import { syncUiSimulationTime } from '../features/simulation/simulationSlice';
import { liveServerTimeS, nextLiveClockS } from '../features/simulation/liveClock';
import { useAppDispatch, useAppSelector } from './redux';
import { simulationClockStore } from '../store/simulationClockStore';
import { replaySnapshotStore } from '../store/replaySnapshotStore';
import { liveOhtRuntimeStore } from '../store/liveOhtRuntimeStore';
import { liveInventoryDisplayStore } from '../store/liveInventoryDisplayStore';
import { liveStreamInventoryStore } from '../store/liveStreamInventoryStore';
import { liveMotionStore } from '../store/liveMotionStore';
import { resolveDerivedGateModules } from '../utils/derivedGateEquipment';
import { LiveRailIndex } from '../features/simulation/liveRailPose';

/** Cap per-frame jump while the tab is visible (avoids huge hitch teleports). */
const MAX_FRAME_DELTA_S = 0.25;
/**
 * Background tabs throttle or pause rAF. Allow larger steps on the hidden
 * interval / visibility catch-up so multi-FOUP runs keep advancing.
 */
const MAX_BACKGROUND_DELTA_S = 2;
/** Fallback ticker while `document.hidden` (rAF is frozen in other windows). */
const BACKGROUND_TICK_MS = 250;
/** Scrubber / panel sync — slower on purpose so React does not hitch 2D Konva. */
const UI_SYNC_INTERVAL_MS = 200;

/** Runs the single authoritative clock for backend playback (rAF + background fallback). */
export function useSimulationPlaybackClock(): void {
  const dispatch = useAppDispatch();
  const running = useAppSelector((state) => state.simulation.running);
  const source = useAppSelector((state) => state.simulation.source);
  const playing = useAppSelector((state) => state.simulation.playing);
  const paused = useAppSelector((state) => state.simulation.paused);
  const finished = useAppSelector((state) => state.simulation.finished);
  const durationS = useAppSelector((state) => state.simulation.durationS);
  const playbackSpeed = useAppSelector((state) => state.simulation.playbackSpeed);
  const replayPlan = useAppSelector((state) => state.simulation.replayPlan);
  const liveAnchorS = useAppSelector((state) => state.simulation.live.serverTimeAnchorS);
  const liveAnchorWallMs = useAppSelector((state) => state.simulation.live.serverTimeAnchorWallMs);
  const liveRate = useAppSelector((state) => state.simulation.live.rateWindow.observedRate);
  const liveInventory = useAppSelector((state) => state.simulation.live.inventory);
  const lastStockerDrops = useAppSelector((state) => state.simulation.live.lastStockerDrops);
  const liveGates = liveInventory?.gates;
  const layoutElements = useAppSelector((state) => state.layout.elements);
  const trackNodeAnnotations = useAppSelector(
    (state) => state.layout.trackNodeAnnotations
  );
  const derivedGateModules = useMemo(
    () =>
      resolveDerivedGateModules(
        Object.values(layoutElements),
        trackNodeAnnotations
      ),
    [layoutElements, trackNodeAnnotations]
  );
  const liveRailIndex = useMemo(
    () => (source === 'live' ? new LiveRailIndex(Object.values(layoutElements)) : null),
    [layoutElements, source]
  );
  const lastUiSyncMsRef = useRef(0);

  // The live stream re-anchors ~10x per second and pushes `durationS` out with
  // it. Read both through refs so the rAF loop is never torn down and rebuilt
  // mid-playback — restarting it dropped a frame and forced an extra Redux sync
  // on every incoming frame.
  const liveRef = useRef({ anchorS: liveAnchorS, anchorWallMs: liveAnchorWallMs, rate: liveRate });
  const durationRef = useRef(durationS);

  useEffect(() => {
    liveRef.current = { anchorS: liveAnchorS, anchorWallMs: liveAnchorWallMs, rate: liveRate };
  }, [liveAnchorS, liveAnchorWallMs, liveRate]);

  useEffect(() => {
    durationRef.current = durationS;
  }, [durationS]);

  useEffect(() => {
    replaySnapshotStore.setAutoSeekEnabled(source !== 'live');
    replaySnapshotStore.setPlan(replayPlan, simulationClockStore.getTime());
  }, [replayPlan, source]);

  useEffect(() => {
    if (source === 'live') {
      // Timelines are applied by liveMotionStore on each flush. This effect only
      // wires gate inventory / modules and clears the scheduler when leaving live.
      liveOhtRuntimeStore.setGateInventory(liveGates ?? []);
      liveOhtRuntimeStore.setGateModules(derivedGateModules);
      liveOhtRuntimeStore.setRailIndex(liveRailIndex);
      liveOhtRuntimeStore.setTimelines(liveMotionStore.getOhtTimelines());
    } else {
      liveOhtRuntimeStore.reset();
    }
  }, [derivedGateModules, liveGates, liveRailIndex, source]);

  useEffect(() => {
    const snapshot = source === 'live' ? (liveInventory ?? null) : null;
    liveInventoryDisplayStore.setRaw(snapshot);
    liveStreamInventoryStore.setRaw(snapshot);
  }, [liveInventory, source]);

  useEffect(() => {
    if (source === 'live') {
      liveInventoryDisplayStore.setLastStockerDrops(lastStockerDrops);
    } else {
      liveInventoryDisplayStore.setLastStockerDrops({});
    }
  }, [lastStockerDrops, source]);

  useEffect(() => {
    if (!running || !playing || paused || finished) return;

    let rafId: number | null = null;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let previousTimestamp: number | null = null;
    lastUiSyncMsRef.current = 0;

    const advance = (timestamp: number, background: boolean) => {
      if (previousTimestamp === null) previousTimestamp = timestamp;
      const rawDeltaS = (timestamp - previousTimestamp) / 1000;
      previousTimestamp = timestamp;

      const maxDelta = background ? MAX_BACKGROUND_DELTA_S : MAX_FRAME_DELTA_S;
      const deltaS = Math.min(maxDelta, Math.max(0, rawDeltaS));
      const durationS = durationRef.current;
      let nextTime: number;
      if (source === 'live') {
        const live = liveRef.current;
        nextTime = nextLiveClockS({
          currentS: simulationClockStore.getTime(),
          serverS: liveServerTimeS(live.anchorS, live.anchorWallMs, timestamp, live.rate),
          wallDeltaS: deltaS,
          rate: live.rate,
        });
      } else {
        nextTime = Math.min(durationS, simulationClockStore.getTime() + deltaS * playbackSpeed);
      }
      simulationClockStore.setTime(nextTime, deltaS);

      const shouldSyncUi =
        lastUiSyncMsRef.current === 0 ||
        timestamp - lastUiSyncMsRef.current >= UI_SYNC_INTERVAL_MS ||
        nextTime >= durationS;
      if (shouldSyncUi) {
        lastUiSyncMsRef.current = timestamp;
        dispatch(syncUiSimulationTime(nextTime));
      }

      return source === 'live' || nextTime < durationS;
    };

    const stopRaf = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    const stopInterval = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const scheduleVisible = () => {
      stopInterval();
      const tick = (timestamp: number) => {
        if (typeof document !== 'undefined' && document.hidden) return;
        const cont = advance(timestamp, false);
        if (cont) rafId = requestAnimationFrame(tick);
        else rafId = null;
      };
      rafId = requestAnimationFrame(tick);
    };

    const scheduleHidden = () => {
      stopRaf();
      // Reset baseline so the first hidden tick does not apply a huge rAF gap.
      previousTimestamp = performance.now();
      intervalId = setInterval(() => {
        if (typeof document !== 'undefined' && !document.hidden) return;
        const cont = advance(performance.now(), true);
        if (!cont) stopInterval();
      }, BACKGROUND_TICK_MS);
    };

    const onVisibility = () => {
      if (document.hidden) {
        scheduleHidden();
        return;
      }
      // Catch up any wall time missed while switching back to this window.
      const now = performance.now();
      if (previousTimestamp !== null) {
        advance(now, true);
      }
      previousTimestamp = now;
      if (source === 'live' || simulationClockStore.getTime() < durationRef.current) {
        scheduleVisible();
      }
    };

    if (typeof document !== 'undefined' && document.hidden) {
      scheduleHidden();
    } else {
      scheduleVisible();
    }

    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stopRaf();
      stopInterval();
      dispatch(syncUiSimulationTime(simulationClockStore.getTime()));
    };
  }, [dispatch, finished, paused, playbackSpeed, playing, running, source]);
}
